import { base } from '@workday/canvas-tokens-web'
import { textStyles } from '../text-styles'

const expandedButtonStyles = {
  color: 'var(--vndly-color-container-dark) !important',
  bg: 'system.color.bg.alt.default',
  '.menu-button__icon': {
    color: 'system.color.fg.muted.stronger'
  }
}
const variantMainMenu = {
  button: {
    ...textStyles.bodySmallSemi,
    display: 'flex',
    alignItems: 'center',
    color: 'system.color.fg.default',
    py: 3,
    px: 3,
    rounded: 4,
    _hover: {
      ...expandedButtonStyles
    },
    _focus: {
      boxShadow: 'none'
    },
    _focusVisible: {
      boxShadow: 'none',
      '[data-using-keyboard] &': {
        ...expandedButtonStyles,
        boxShadow: 'outline'
      }
    },
    _expanded: {
      // Only give buttons a flat bottom, because they open a menu
      // Top level menu buttons that do not open a menu use the `<a>` tag
      'button&': {
        roundedBottom: 0
      },
      ...expandedButtonStyles,
      '.chevron-icon': {
        color: 'system.color.fg.muted.stronger',
        transform: 'rotate(-180deg)'
      }
    },
    transition: 'background .1s ease-out',
    // Menu Button Left Icon
    '.menu-button__icon': {
      color: 'system.color.fg.muted.soft',
      w: 5,
      h: 5,
      mr: { base: 2, lg: 1 },
      position: 'relative',
      top: '-1px'
    },
    // Menu Button Right Chevron Icon
    '.chevron-icon': {
      w: 4,
      h: 4,
      ml: 1,
      transition: 'transform .3s'
    }
  },
  list: {
    bg: 'system.color.bg.default',
    borderColor: 'system.color.border.container',
    roundedTopLeft: 0,
    boxShadow: 'xl'
  },
  item: {
    color: 'system.color.fg.default',
    fontWeight: 'normal',
    fontSize: 'sm',
    // Menu Item Left Icon
    '.chakra-menu__icon': { color: 'system.color.fg.muted.soft', w: 5, h: 5 },
    _hover: {
      textDecoration: 'none',
      boxShadow: 'none',
      bg: 'system.color.bg.alt.default',
      color: 'var(--vndly-color-container-dark)  !important',
      // Menu Item Left Icon
      '.chakra-menu__icon': { color: 'system.color.fg.muted.stronger' }
    },
    _focusVisible: {
      '[data-using-keyboard] &': {
        boxShadow: 'outline'
      }
    },
    _focus: {
      color: 'system.color.fg.default'
    }
  }
}

const variantProfileMenu = {
  button: {
    '&[data-is-impersonated="true"]': {
      background: {
        base: 'brand.error.dark',
        sm: `repeating-linear-gradient(120deg, var(${base.neutral900}), var(${base.neutral900}) 10px, var(${base.red700}) 10px, var(${base.red700}) 20px)`
      },
      color: 'system.color.fg.inverse',
      '.profile-button__label': {
        color: 'system.color.fg.inverse',
        // Creates a soft red outline around the text to make it more readable
        textShadow:
          '-2px -2px 1px #a31c12, 2px -2px 1px #a31c12, -2px 2px 1px #a31c12, 2px 2px 1px #a31c12, 0px -2px 2px #a31c12, 0px 2px 2px #a31c12, -2px 0px 2px #a31c12, 2px 0px 2px #a31c12;'
      },
      '.chevron-icon': {
        color: 'system.color.fg.inverse'
      },
      '.profile-avatar-container': {
        borderColor: 'system.color.fg.strong',
        boxShadow: { base: 'none', lg: `0 0 0 3px var(${base.red700})` }
      }
    },
    rounded: 4,
    px: 0,
    display: 'flex',
    alignItems: 'center',
    color: 'system.color.fg.default',
    borderWidth: '6px 0 6px 0',
    borderColor: 'transparent',
    boxSizing: 'content-box',
    gridGap: 2,
    '.chakra-button__icon': { m: 0 },
    '.chevron-icon': {
      color: 'system.color.fg.muted.soft',
      w: 4,
      h: 4,
      display: { base: 'none', sm: 'block' },
      transition: 'transform .3s'
    },
    _focus: {
      boxShadow: 'none',
      '[data-using-keyboard] &': {
        boxShadow: 'outline'
      }
    },
    _expanded: {
      '.chevron-icon': {
        transform: 'rotate(-180deg)'
      }
    },
    '.profile-button__label': {
      ...textStyles.bodySmallSemi,
      color: 'system.color.fg.default',
      maxW: '17ch',
      m: 0
    },
    '.profile-avatar-container': {
      bg: 'brand.neutral.lighter',
      position: 'relative',
      zIndex: 1
    },
    '.profile-avatar': {
      color: 'system.color.fg.muted.soft'
    },
    '@media (min-width: 0px) and (max-width: 600px)': { '.profile-button__label': { display: 'none' } },
    '@media (min-width: 1350px) and (max-width: 1450px)': { '.profile-button__label': { display: 'none' } }
  },
  list: {
    ...variantMainMenu.list,
    rounded: 4,
    roundedTopLeft: 4
  },
  item: {
    ...variantMainMenu.item
  }
}

const variantNavigationSubMenu = {
  button: variantProfileMenu.item,
  list: {
    boxShadow: '2xl'
  },
  item: variantProfileMenu.item
}

const variantOverflowMenu = {
  button: {
    color: 'gray.500',
    mb: '0',
    py: '4',
    px: '2',
    _hover: {
      color: 'gray.800'
    }
  },
  list: {
    borderRadius: 'base',
    zIndex: '10'
  },
  item: {
    p: 0,
    'a, button': {
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'flex-start',
      p: '2',
      _selected: {
        bgColor: 'gray.200'
      },
      _hover: {
        border: 'none'
      }
    },
    _hover: {
      bgColor: 'gray.100'
    }
  }
}

export const Menu = {
  baseStyle: {
    item: {
      py: 2,
      px: 4,
      '&.delete-menu-item': {
        color: 'var(--vndly-color-link-delete)',
        justifyContent: 'flex-start',
        fontSize: 'var(--vndly-font-size-md)',
        fontWeight: 'var(--vndly-font-weight-semibold)',
        lineHeight: 'base'
      },
      _active: {
        borderColor: 'var(--vndly-color-container-border-focused)',
        borderWidth: '1px'
      }
    }
  },
  variants: {
    mainMenu: variantMainMenu,
    profileMenu: variantProfileMenu,
    navigationSubMenu: variantNavigationSubMenu,
    overflowMenu: variantOverflowMenu
  }
}
